/**
 * RecommendedActionsCard.jsx — Standardized "what should I do next?" surface.
 *
 * Each module renders the same component but tailors `title` and `actions`:
 *   - PeopleLab : "Recommended Manager Actions"
 *   - MarketLab : "Recommended Go-To-Market Actions"
 *   - PharmaLab : "Recommended Research Actions"
 *
 * Each action has 6 slots (sparse — empty slots are hidden):
 *   - action  : short imperative ("Run change-management workshop")
 *   - target  : segment/audience the action applies to
 *   - why     : one-sentence justification (links back to a metric/segment)
 *   - owner   : suggested owner (HR Lead / GTM Lead / Clinical Ops…)
 *   - timing  : when to act (Within 2 weeks / Pre-launch / Quarterly…)
 *   - evidence: bullet list of metric/segment refs that drove the action
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckSquare, ChevronRight, Target as TargetIcon, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import EmptyState from './EmptyState';

const PRIORITY_TONE = {
  high: 'border-l-red-500 bg-negative-soft/30 ',
  medium: 'border-l-amber-400 bg-warning-soft/30 ',
  low: 'border-l-slate-300 dark:border-l-slate-600 bg-white dark:bg-card',
};

const PRIORITY_LABEL = { high: 'High priority', medium: 'Medium', low: 'Low' };

export default function RecommendedActionsCard({
  title = 'Recommended Actions',
  subtitle = 'What to do next based on this report',
  actions = [],
  className,
  testId = 'recommended-actions',
}) {
  const filtered = (actions || []).filter((a) => a && a.action);

  if (filtered.length === 0) {
    return (
      <Card className={cn('border border-slate-200 dark:border-border', className)} data-testid={testId}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-slate-400" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={CheckSquare}
            title="No specific actions surfaced for this report"
            description="When segment differences, low reliability, or notable warnings appear, recommended actions will be listed here."
            testId={`${testId}-empty`}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border border-slate-200 dark:border-border', className)} data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-slate-400" />
            {title}
          </CardTitle>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">
            {filtered.length} action{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.slice(0, 5).map((a, i) => (
            <ActionRow key={i} action={a} testId={`${testId}-item-${i}`} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ActionRow({ action, testId }) {
  const tone = PRIORITY_TONE[action.priority || 'low'];
  const priorityLabel = PRIORITY_LABEL[action.priority || 'low'];

  return (
    <div
      className={cn(
        'border border-slate-200 dark:border-border rounded-lg p-4 border-l-4 transition-colors',
        tone
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">
            {action.action}
          </p>
        </div>
        {action.priority && (
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] font-semibold flex-shrink-0',
              action.priority === 'high'
                ? 'text-negative-soft-foreground bg-negative-soft border-negative-border'
                : action.priority === 'medium'
                ? 'text-warning-soft-foreground bg-warning-soft border-warning-border'
                : 'text-slate-500 bg-slate-50 border-slate-200'
            )}
          >
            {priorityLabel}
          </Badge>
        )}
      </div>

      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        {action.target && (
          <Slot label="Target" value={action.target} icon={<TargetIcon className="w-3 h-3 text-slate-400" />} />
        )}
        {action.owner && <Slot label="Owner" value={action.owner} />}
        {action.timing && <Slot label="Timing" value={action.timing} />}
      </div>

      {action.why && (
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          <span className="text-slate-400">Why:</span> {action.why}
        </p>
      )}

      {action.evidence?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Evidence
          </p>
          <ul className="space-y-0.5">
            {action.evidence.map((e, i) => (
              <li key={i} className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                · {e}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Slot({ label, value, icon }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1">
        {icon} {label}
      </span>
      <span className="text-xs text-slate-700 dark:text-slate-200 font-medium truncate" title={value}>
        {value}
      </span>
    </div>
  );
}
